import { Navigate, NavLink, Outlet, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Users, BriefcaseBusiness, Package, Receipt, Settings, LogOut, Menu } from 'lucide-react';
import { useState } from 'react';
import { useAuthStore, hasPermission } from './store';
import { Login } from './pages/Login';
import { Dashboard } from './pages/Dashboard';
import { ModulePage } from './pages/ModulePage';
import { CustomerPage } from './pages/CustomerPage';
import { ProductPage } from './pages/ProductPage';
import { CategoryPage } from './pages/CategoryPage';
import { InventoryPage } from './pages/InventoryPage';
import { StockPage } from './pages/StockPage';
import { SalesPage } from './pages/SalesPage';
import { ReportsPage } from './pages/ReportsPage';
import { SettingsPage } from './pages/SettingsPage';
import { PublicSite } from './pages/PublicSite';

function Shell() {
  const nav = useNavigate();
  const clear = useAuthStore((s) => s.clear);
  const [open, setOpen] = useState(false);
  const items = [
    { to: '/app', label: 'Dashboard', Icon: LayoutDashboard, permission: 'dashboard:read', hideForStaff: true },
    { to: '/app/customers', label: 'Customers', Icon: Users, permission: 'customers:read' },
    { to: '/app/jobs', label: 'Jobs', Icon: BriefcaseBusiness, permission: 'jobs:read' },
    { to: '/app/products', label: 'Products', Icon: Package, permission: 'inventory:read' },
    { to: '/app/categories', label: 'Categories', Icon: Package, permission: 'inventory:read' },
    { to: '/app/inventory', label: 'Inventory', Icon: Package, permission: 'inventory:read' },
    { to: '/app/stock', label: 'Stock', Icon: Package, permission: 'inventory:read' },
    { to: '/app/sales', label: 'Sales', Icon: Receipt, permission: 'sales:read' },
    { to: '/app/reports', label: 'Reports', Icon: Receipt, permission: 'sales:read' },
    { to: '/app/settings', label: 'Settings', Icon: Settings, permission: 'settings:read' },
  ] as const;

  const visibleItems = items.filter((item) => hasPermission(item.permission) && !('hideForStaff' in item && !hasPermission('sales:read')));

  return <div className="os-shell"><aside className={open ? 'sidebar open' : 'sidebar'}><div className="brand"><span className="brand-mark">A</span><span>AUDIOGRAPH <em>OS</em></span></div><nav>{visibleItems.map(({ to, label, Icon }) => <NavLink key={to} to={to} onClick={() => setOpen(false)} className={({ isActive }) => isActive ? 'nav-link active' : 'nav-link'}><Icon size={17} />{label}</NavLink>)}</nav><button className="logout" onClick={() => { clear(); nav('/login'); }}><LogOut size={16} /> Sign out</button></aside><main className="main"><header className="topbar"><button className="menu" onClick={() => setOpen(!open)} aria-label="Toggle navigation"><Menu size={20} /></button><div><p className="eyebrow">OPERATIONS SYSTEM / V1.0</p><h1>Command center</h1></div><div className="status"><span className="pulse" /> Systems operational</div></header><div className="content"><Outlet /></div></main></div>;
}

function Protected() {
  const token = useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  const location = useLocation();
  if (!token) return <Navigate to="/login" replace />;
  // Staff land on Products instead of the Dashboard, which is reserved for
  // owners/admins/managers. The redirect fires only on paths staff must not
  // stay on, so it can never fire twice in a row (which would loop into a
  // blank screen). At /app/products and other authorized inventory routes,
  // the shell renders normally.
  if (
    user?.role === "staff" &&
    ["/app", "/app/sales", "/app/reports", "/app/settings"].includes(location.pathname)
  ) {
    return <Navigate to="/app/products" replace />;
  }
  return <Shell />;
}

const publicRoutes = [
  '/',
  '/index.html',
  '/contact',
  '/contact.html',
  '/gallery',
  '/gallery.html',
  '/services',
  '/services.html',
  '/services/android-head-unit-installation-cavite',
  '/services/android-head-unit-installation-cavite.html',
  '/services/wireless-carplay-android-auto',
  '/services/wireless-carplay-android-auto.html',
  '/services/360-reverse-camera-installation',
  '/services/360-reverse-camera-installation.html',
  '/services/dashcam-car-audio-installation',
  '/services/dashcam-car-audio-installation.html',
];

export default function App() {
  return <Routes>
    {publicRoutes.map((path) => <Route key={path} path={path} element={<PublicSite />} />)}
    <Route path="/login" element={<Login />} />
    <Route element={<Protected />}>
      <Route path="/app" element={<Dashboard />} />
      <Route path="/app/customers" element={<CustomerPage />} />
      <Route path="/app/products" element={<ProductPage />} />
      <Route path="/app/categories" element={<CategoryPage />} />
      <Route path="/app/inventory" element={<InventoryPage />} />
      <Route path="/app/stock" element={<StockPage />} />
      <Route path="/app/sales" element={hasPermission('sales:read') ? <SalesPage /> : <Navigate to="/app" replace />} />
      <Route path="/app/reports" element={hasPermission('sales:read') ? <ReportsPage /> : <Navigate to="/app" replace />} />
      <Route path="/app/settings" element={hasPermission('settings:read') ? <SettingsPage /> : <Navigate to="/app" replace />} />
      <Route path="/app/:module" element={<ModulePage />} />
    </Route>
    <Route path="*" element={<Navigate to="/" replace />} />
  </Routes>;
}
